import * as actionTypes from '../../constants/actionTypes';
import * as notificationsTypes from '../../constants/notificationsTypes';

import { CALL_PRIVATE_API } from '../../middleware/api';
import {
  notificationTypeSchema,
  notificationChannelSchema,
  notificationSettingSchema,
  notificationSchema,
} from '../../schemas';

import { addPaginatedResource } from '../pagination';
import { RESOURCE_NOTIFICATIONS_PAGINATED } from '../../constants/paginationEntitiesTypes';

import { GET, POST, PUT, DELETE } from '../../constants/requestMethods';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import { normalize } from 'normalizr';

const fetchAllNotifications = (
  type,
  id,
  page,
  pageSize,
  sort_by = 'created_at',
  sort_order = 'DESC',
) => {
  let payload = {
    resource_type: type,
    page: page,
    per_page: pageSize,
    sort_by: sort_by,
    sort_order: sort_order,
  };

  if (id) {
    payload.resource_id = id;
  }

  let types;
  switch (type) {
    case notificationsTypes.SOURCE:
      types = [
        actionTypes.GET_ALL_NOTIFICATIONS_SOURCE_REQUEST,
        actionTypes.GET_ALL_NOTIFICATIONS_SOURCE_SUCCESS,
        actionTypes.GET_ALL_NOTIFICATIONS_SOURCE_FAILURE,
      ];
      break;

    case notificationsTypes.DATASET:
      types = [
        actionTypes.GET_ALL_NOTIFICATIONS_SET_REQUEST,
        actionTypes.GET_ALL_NOTIFICATIONS_SET_SUCCESS,
        actionTypes.GET_ALL_NOTIFICATIONS_SET_FAILURE,
      ];
      break;
    case notificationsTypes.ALERT:
      types = [
        actionTypes.GET_ALL_NOTIFICATIONS_ALERT_REQUEST,
        actionTypes.GET_ALL_NOTIFICATIONS_ALERT_SUCCESS,
        actionTypes.GET_ALL_NOTIFICATIONS_ALERT_FAILURE,
      ];

      payload = {
        'level[]': 'ERROR',
        page,
        per_page: pageSize,
        sort_by: 'created_at',
        sort_order,
      };
      break;
    case notificationsTypes.UNREAD:
      types = [
        actionTypes.GET_UNREAD_NOTIFICATIONS_REQUEST,
        actionTypes.GET_UNREAD_NOTIFICATIONS_SUCCESS,
        actionTypes.GET_UNREAD_NOTIFICATIONS_FAILURE,
      ];

      payload = {
        read: 0,
        page,
        per_page: pageSize,
        sort_by: 'created_at',
        sort_order,
      };
      break;
    default:
      types = [
        actionTypes.GET_ALL_NOTIFICATIONS_REQUEST,
        actionTypes.GET_ALL_NOTIFICATIONS_SUCCESS,
        actionTypes.GET_ALL_NOTIFICATIONS_FAILURE,
      ];

      payload = {
        page: page,
        per_page: pageSize,
        sort_by: sort_by,
        sort_order: sort_order,
      };
  }
  return {
    [CALL_PRIVATE_API]: {
      types,
      endpoint: `/notifications`,
      schema: [notificationSchema],
      payload,
      requestMethod: GET,
      requestInfo: { ...payload, type },
    },
  };
};

const fetchUnreadNotifications = () => ({
  [CALL_PRIVATE_API]: {
    types: [
      actionTypes.GET_UNREAD_COUNTER_NOTIFICATIONS_REQUEST,
      actionTypes.GET_UNREAD_COUNTER_NOTIFICATIONS_SUCCESS,
      actionTypes.GET_UNREAD_COUNTER_NOTIFICATIONS_FAILURE,
    ],
    endpoint: `/notifications/count`,
    schema: [notificationSchema],
    requestMethod: GET,
    payload: {
      read: 0,
    },
  },
});

const updateNotificationAsRead = (id) => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_NOTIFICATION_AS_READ,
        actionTypes.UPDATE_NOTIFICATION_AS_READ_SUCCESS,
        actionTypes.UPDATE_NOTIFICATION_AS_READ_FAILURE,
      ],
      endpoint: `/notifications/${id}/mark_read`,
      payload: { read: true },
      schema: [notificationSchema],
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  }).then(() => {
    dispatch({
      type: 'UPDATE_LOCAL_NOTIFICATION',
      ...normalize(
        [
          {
            ...getState().entities?.notifications?.[id],
            read_at: new Date().toDateString(),
          },
        ],
        [notificationSchema],
      ),
      schema: [notificationSchema],
    });
  });
};

const updateAllNotificationAsRead = () => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_ALL_NOTIFICATIONS_AS_READ,
        actionTypes.UPDATE_ALL_NOTIFICATIONS_AS_READ_SUCCESS,
        actionTypes.UPDATE_ALL_NOTIFICATIONS_AS_READ_FAILURE,
      ],
      endpoint: `/notifications/mark_read`,
      payload: { notification_id: 'all' },
      schema: [notificationSchema],
      requestMethod: PUT,
    },
  }).then(() => {
    const state = getState();
    dispatch({
      type: 'UPDATE_LOCAL_NOTIFICATION',
      ...normalize(
        state?.notifications?.ALL?.ids?.map((id) => ({
          ...state.entities?.notifications[id],
          read_at: new Date().toDateString(),
        })),
        [notificationSchema],
      ),
      schema: [notificationSchema],
    });
  });
};

const fetchNotificationsTypes = () => ({
  [CALL_PRIVATE_API]: {
    types: [
      actionTypes.GET_NOTIFICATIONS_TYPES_REQUEST,
      actionTypes.GET_NOTIFICATIONS_TYPES_SUCCESS,
      actionTypes.GET_NOTIFICATIONS_TYPES_FAILURE,
    ],
    endpoint: `/notification_types`,
    schema: [notificationTypeSchema],
    requestMethod: GET,
  },
});

const fetchNotificationsChannels = () => ({
  [CALL_PRIVATE_API]: {
    types: [
      actionTypes.GET_NOTIFICATIONS_CHANNEL_REQUEST,
      actionTypes.GET_NOTIFICATIONS_CHANNEL_SUCCESS,
      actionTypes.GET_NOTIFICATIONS_CHANNEL_FAILURE,
    ],
    endpoint: `/notification_channel_settings`,
    schema: [notificationChannelSchema],
    requestMethod: GET,
  },
});

const fetchNotificationsSettings = (resourceType, resourceId) => {
  const payload = {
    notification_resource_type: resourceType,
  };

  if (resourceId > 0) {
    payload.resource_id = resourceId;
  }

  return {
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_NOTIFICATIONS_SETTINGS_REQUEST,
        actionTypes.GET_NOTIFICATIONS_SETTINGS_SUCCESS,
        actionTypes.GET_NOTIFICATIONS_SETTINGS_FAILURE,
      ],
      endpoint: `/notification_settings`,
      schema: [notificationSettingSchema],
      requestMethod: GET,
      payload,
      requestInfo: {
        resourceType,
        resourceId,
      },
    },
  };
};

const createNotificationChannel =
  (channel, config = {}) =>
  (dispatch) => {
    let payload = {
      channel,
      config,
    };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_NOTIFICATIONS_CHANNEL_REQUEST,
          actionTypes.CREATE_NOTIFICATIONS_CHANNEL_SUCCESS,
          actionTypes.CREATE_NOTIFICATIONS_CHANNEL_FAILURE,
        ],
        endpoint: `/notification_channel_settings`,
        schema: notificationChannelSchema,
        requestMethod: POST,
        payload: payload,
      },
    });
  };

export const updateNotificationChannel = (id, channel, config) => (dispatch) => {
  if (!id) {
    return dispatch(createNotificationChannel(channel, config));
  }

  let payload = {
    config,
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_NOTIFICATIONS_CHANNEL_REQUEST,
        actionTypes.UPDATE_NOTIFICATIONS_CHANNEL_SUCCESS,
        actionTypes.UPDATE_NOTIFICATIONS_CHANNEL_FAILURE,
      ],
      endpoint: `/notification_channel_settings/${id}`,
      schema: notificationChannelSchema,
      requestMethod: PUT,
      payload,
    },
  });
};

const createNotificationSetting =
  (resourceType, resourceId, typeId, channel, channelId, checked, config) => (dispatch) => {
    let payload = {
      notification_resource_type: resourceType,
      resource_id: resourceId,
      channel: channel,
      notification_channel_setting_id: channelId,
      notification_type_id: typeId,
      checked: '' + checked,
    };

    if (config) {
      payload.config = config;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_NOTIFICATIONS_SETTINGS_REQUEST,
          actionTypes.CREATE_NOTIFICATIONS_SETTINGS_SUCCESS,
          actionTypes.CREATE_NOTIFICATIONS_SETTINGS_FAILURE,
        ],
        endpoint: `/notification_settings`,
        schema: notificationSettingSchema,
        requestMethod: POST,
        payload: payload,
        requestInfo: {
          typeId,
          channel,
        },
      },
    });
  };

const updateNotificationSetting =
  (id, resourceType, resourceId, typeId, channel, channelId, checked, config) => (dispatch) => {
    let payload = {
      notification_resource_type: resourceType,
      resource_id: resourceId,
      channel: channel,
      notification_channel_setting_id: channelId,
      notification_type_id: typeId,
      checked: '' + checked,
    };

    if (config) {
      payload.config = config;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_NOTIFICATIONS_SETTINGS_REQUEST,
          actionTypes.UPDATE_NOTIFICATIONS_SETTINGS_SUCCESS,
          actionTypes.UPDATE_NOTIFICATIONS_SETTINGS_FAILURE,
        ],
        endpoint: `/notification_settings/${id}`,
        schema: notificationSettingSchema,
        requestMethod: PUT,
        payload: payload,
        requestInfo: {
          typeId,
          channel,
        },
      },
    });
  };

export const deleteResourceNotificationSetting =
  (id, resourceType = null, resourceId = null) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.DELETE_NOTIFICATIONS_SETTINGS_REQUEST,
          actionTypes.DELETE_NOTIFICATIONS_SETTINGS_SUCCESS,
          actionTypes.DELETE_NOTIFICATIONS_SETTINGS_FAILURE,
        ],
        endpoint: `/notification_settings/${id}`,
        schema: null,
        requestMethod: DELETE,
        requestInfo: {
          id,
          resourceType,
          resourceId,
        },
      },
    });
  };

export const loadAllNotifications =
  (type, pageSize, resourceId = 0, page = 0) =>
  (dispatch, getState) => {
    let { notifications } = getState();
    return dispatch(
      fetchAllNotifications(type, resourceId, page || notifications[type].page, pageSize),
    );
  };

export const loadUnreadNotifications = () => (dispatch, getState) => {
  return dispatch(fetchUnreadNotifications());
};

export const toggleNotificationsList = () => {
  return {
    type: actionTypes.TOGGLE_NOTIFICATIONS_LIST_VIEW,
  };
};

export const markNotificationAsRead = (id) => (dispatch, getState) => {
  return dispatch(updateNotificationAsRead(id));
};

export const markAllNotificationsAsRead = () => (dispatch, getState) => {
  return dispatch(updateAllNotificationAsRead());
};

export const loadNotificationsTypes = () => (dispatch) => {
  return dispatch(fetchNotificationsTypes());
};

export const loadNotificationsChannels = () => (dispatch) => {
  return dispatch(fetchNotificationsChannels());
};

export const loadNotificationsSettings =
  (resourceType = 'USER', resourceId = 0) =>
  (dispatch) => {
    return dispatch(fetchNotificationsSettings(resourceType, resourceId));
  };

export const updateNotificationManager =
  (resourceType = 'USER', resourceId, typeId, channel, checked, settingId = false, config = null) =>
  (dispatch, getState) => {
    let {
      notificationsManager: { notification_channels_enum_map, notification_settings },
    } = getState();
    let channelId = notification_channels_enum_map[channel];
    let setting = (notification_settings[typeId] || {})[channel];
    let effectiveSettingId = settingId === false ? setting && setting.id : settingId;
    if (!channelId) {
      return dispatch(createNotificationChannel(channel)).then((response) => {
        const { result } = response;
        if (effectiveSettingId) {
          return dispatch(
            updateNotificationSetting(
              effectiveSettingId,
              resourceType,
              resourceId,
              typeId,
              channel,
              result,
              checked,
              config,
            ),
          );
        } else {
          return dispatch(
            createNotificationSetting(
              resourceType,
              resourceId,
              typeId,
              channel,
              result,
              checked,
              config,
            ),
          );
        }
      });
    } else {
      if (effectiveSettingId) {
        return dispatch(
          updateNotificationSetting(
            effectiveSettingId,
            resourceType,
            resourceId,
            typeId,
            channel,
            channelId,
            checked,
            config,
          ),
        );
      } else {
        return dispatch(
          createNotificationSetting(
            resourceType,
            resourceId,
            typeId,
            channel,
            channelId,
            checked,
            config,
          ),
        );
      }
    }
  };

export const prevNotificationPage = (type) => {
  return {
    type: actionTypes.PREV_NOTIFICATION_PAGE,
    payload: {
      type,
    },
  };
};

export const nextNotificationPage = (type) => {
  return {
    type: actionTypes.NEXT_NOTIFICATION_PAGE,
    payload: {
      type,
    },
  };
};

const updateResourceNotificationAsRead = (id, resourceType, resourceId) => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_RESOURCE_NOTIFICATION_AS_READ,
        actionTypes.UPDATE_RESOURCE_NOTIFICATION_AS_READ_SUCCESS,
        actionTypes.UPDATE_RESOURCE_NOTIFICATION_AS_READ_FAILURE,
      ],
      endpoint: `/notifications/${id}/mark_read`,
      payload: { read: true },
      schema: null,
      requestMethod: PUT,
      requestInfo: {
        id,
        resourceType,
        resourceId,
        paginatedResource: RESOURCE_NOTIFICATIONS_PAGINATED,
      },
    },
  }).then(() => {
    return dispatch({
      type: 'UPDATE_LOCAL_NOTIFICATION',
      ...normalize(
        [
          {
            ...getState().entities?.notifications?.[id],
            read_at: new Date().toDateString(),
          },
        ],
        [notificationSchema],
      ),
      schema: [notificationSchema],
    });
  });
};

export const markResourceNotificationAsRead =
  (id, resourceType, resourceId = null) =>
  (dispatch, getState) => {
    return dispatch(updateResourceNotificationAsRead(id, resourceType, resourceId));
  };

export const deleteResourceNotification =
  (id, resourceType = null, resourceId = null) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.DELETE_RESOURCE_NOTIFICATION_REQUEST,
          actionTypes.DELETE_RESOURCE_NOTIFICATION_SUCCESS,
          actionTypes.DELETE_RESOURCE_NOTIFICATION_FAILURE,
        ],
        endpoint: `/notifications/${id}`,
        schema: null,
        requestMethod: DELETE,
        requestInfo: {
          id,
          resourceType,
          resourceId,
          paginatedResource: RESOURCE_NOTIFICATIONS_PAGINATED,
        },
      },
    });
  };

export const fetchResourceNotificationsPaginated =
  (id, resourceType, filters = {}, defaultSortKey = null, aggregate = false) =>
  (dispatch, getState) => {
    const requestInfo = {
      paginatedResource: RESOURCE_NOTIFICATIONS_PAGINATED,
      resourceType,
      aggregate,
      id,
      ...filters,
      defaultSortKey,
    };
    dispatch(addPaginatedResource(requestInfo, actionTypes.GET_RESOURCE_NOTIFICATIONS_SUCCESS));

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_RESOURCE_NOTIFICATIONS_REQUEST,
          actionTypes.GET_RESOURCE_NOTIFICATIONS_SUCCESS,
          actionTypes.GET_RESOURCE_NOTIFICATIONS_FAILURE,
        ],
        endpoint: `/notifications`,
        schema: null,
        requestMethod: GET,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        payload: {
          resource_id: id,
          resource_type: resourceType,
          ...filters,
        },
        requestInfo,
      },
    });
  };
